// src/marketing/content/generations.ts
// Helpers purs du panneau de génération vidéo (Higgsfield). Aucun appel réseau ici.
import type {
  Attachment, Generation, GenerationAspectRatio, GenerationResolution, GenerationStatus,
} from '../../lib/types'

// Backoff documenté par Higgsfield : 2 s, ×1,5, plafond 10 s, jitter.
const POLL_INITIAL_MS = 2000
const POLL_FACTOR = 1.5
const POLL_MAX_MS = 10000
export const POLL_MAX_DURATION_MS = 15 * 60 * 1000

export function nextPollDelay(attempt: number, random: () => number = Math.random): number {
  const base = Math.min(POLL_INITIAL_MS * Math.pow(POLL_FACTOR, attempt), POLL_MAX_MS)
  const jitter = (random() - 0.5) * 0.4 // ±20 %
  return Math.round(base * (1 + jitter))
}

const TERMINAL: readonly GenerationStatus[] = ['completed', 'failed', 'nsfw', 'canceled']

export function isTerminal(status: GenerationStatus): boolean {
  return TERMINAL.includes(status)
}

export function isActive(status: GenerationStatus): boolean {
  return status === 'queued' || status === 'in_progress'
}

/** Réglée = plus rien à attendre du serveur (miroir de isSettled côté route). */
export function isSettled(g: Generation): boolean {
  if (!isTerminal(g.status)) return false
  if (g.status === 'completed') return g.outputAttachmentId !== null
  return true
}

export const GENERATION_STATUS_LABELS: Record<GenerationStatus, string> = {
  queued: 'En attente',
  in_progress: 'En cours',
  completed: 'Terminée',
  failed: 'Échec',
  nsfw: 'Refusée par la modération',
  canceled: 'Annulée',
}

export function modelLabel(model: string): string {
  if (model === 'bytedance/seedance-2.0/reference-to-video') return 'Seedance 2.0 réf.'
  if (model === 'bytedance/seedance-2.0/text-to-video') return 'Seedance 2.0 texte'
  return model
}

export type ReferenceKind = 'image' | 'video' | 'audio'

export const REFERENCE_LIMITS: Record<ReferenceKind, number> = { image: 9, video: 3, audio: 3 }

const IMAGE_TYPES = new Set(['image/jpeg', 'image/png', 'image/webp', 'image/gif'])
const VIDEO_TYPES = new Set(['video/mp4'])
const AUDIO_TYPES = new Set(['audio/wav', 'audio/x-wav', 'audio/wave'])

/** Même règle que functions/lib/higgsfield.ts (dupliquée : pas de runtime partagé src ↔ functions). */
export function classifyReference(contentType: string | null): ReferenceKind | null {
  if (!contentType) return null
  const base = contentType.split(';')[0]?.trim().toLowerCase() ?? ''
  if (IMAGE_TYPES.has(base)) return 'image'
  if (VIDEO_TYPES.has(base)) return 'video'
  if (AUDIO_TYPES.has(base)) return 'audio'
  return null
}

export function countReferences(attachments: Attachment[], selectedIds: string[]): Record<ReferenceKind, number> {
  const counts: Record<ReferenceKind, number> = { image: 0, video: 0, audio: 0 }
  const selected = new Set(selectedIds)
  for (const a of attachments) {
    if (!selected.has(a.id)) continue
    const kind = classifyReference(a.contentType)
    if (kind) counts[kind] += 1
  }
  return counts
}

export const RESOLUTIONS: readonly GenerationResolution[] = ['480p', '720p', '1080p', '4k']
export const ASPECT_RATIOS: readonly GenerationAspectRatio[] = ['9:16', '16:9', '1:1', '4:3', '3:4', '21:9']
